var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { FaBroom, FaSpinner, FaSyncAlt, FaTrash } from 'react-icons/fa';
import { getResetDatabaseStats, resetDatabase } from '../../api/dev';
import { runUploadsGc } from '../../api/images';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import './ResetDbModal.scss';
const formatBytes = (bytes) => {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
function ResetDbModal({ isOpen, onClose }) {
    var _a, _b, _c, _d, _e;
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [gcArmed, setGcArmed] = useState(false);
    const [gcRunning, setGcRunning] = useState(false);
    const [gcDone, setGcDone] = useState(null);
    const [resetArmed, setResetArmed] = useState(false);
    const [resetRunning, setResetRunning] = useState(false);
    const [resetDone, setResetDone] = useState(null);
    const refresh = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        setError(null);
        setGcArmed(false);
        setResetArmed(false);
        try {
            setStats(yield getResetDatabaseStats());
        }
        catch (_a) {
            setError('Failed to load the database stats.');
        }
        finally {
            setLoading(false);
        }
    }), []);
    useEffect(() => {
        if (!isOpen)
            return;
        setStats(null);
        setError(null);
        setGcDone(null);
        setResetDone(null);
        refresh();
    }, [isOpen, refresh]);
    useBodyScrollLock(isOpen);
    if (!isOpen)
        return null;
    const busy = gcRunning || resetRunning;
    const garbage = stats === null || stats === void 0 ? void 0 : stats.garbage;
    const handleGc = () => __awaiter(this, void 0, void 0, function* () {
        if (!gcArmed) {
            setGcArmed(true);
            setResetArmed(false);
            return;
        }
        setGcRunning(true);
        setError(null);
        try {
            setGcDone(yield runUploadsGc());
            yield refresh();
        }
        catch (_a) {
            setError('Cleanup failed — see the backend log.');
        }
        finally {
            setGcRunning(false);
        }
    });
    const handleFullReset = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!resetArmed) {
            setResetArmed(true);
            setGcArmed(false);
            return;
        }
        setResetRunning(true);
        setError(null);
        try {
            const res = yield resetDatabase();
            const summary = Object.entries(res.deleted).map(([k, n]) => `${k}: ${n}`).join(', ');
            setResetDone(`Deleted — ${summary || 'nothing'}. Levels left: ${res.levelsLeft.join(', ') || 'none'}.`);
            yield refresh();
        }
        catch (err) {
            setError(((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || 'Full reset failed.');
        }
        finally {
            setResetRunning(false);
        }
    });
    return (_jsx("div", { className: "modal-overlay", children: _jsxs("div", { className: "modal-content reset-db-modal", children: [_jsxs("div", { className: "rdb-head", children: [_jsxs("h2", { children: [_jsx(FaTrash, {}), " Reset DB"] }), _jsx("button", { className: "rdb-close", onClick: onClose, disabled: busy, "aria-label": "Close", children: "\u00D7" })] }), loading && !stats && _jsxs("p", { className: "rdb-loading", children: [_jsx(FaSpinner, { className: "rdb-spin" }), " Scanning\u2026"] }), stats && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "rdb-section", children: [_jsx("h3", { children: "Garbage \u2014 unused uploaded files" }), _jsx("p", { className: "rdb-sub", children: "Files nothing references any more (replaced art, deleted levels). Safe to delete." }), _jsxs("div", { className: "rdb-stats", children: [_jsxs("div", { children: [_jsx("span", { className: "rdb-num", children: (_a = garbage === null || garbage === void 0 ? void 0 : garbage.files) !== null && _a !== void 0 ? _a : 0 }), _jsx("span", { children: "files total" })] }), _jsxs("div", { children: [_jsx("span", { className: "rdb-num", children: (_b = garbage === null || garbage === void 0 ? void 0 : garbage.referenced) !== null && _b !== void 0 ? _b : 0 }), _jsx("span", { children: "in use" })] }), _jsxs("div", { className: garbage && garbage.orphans > 0 ? 'warn' : '', children: [_jsx("span", { className: "rdb-num", children: (_c = garbage === null || garbage === void 0 ? void 0 : garbage.orphans) !== null && _c !== void 0 ? _c : 0 }), _jsxs("span", { children: ["unused (", formatBytes((_d = garbage === null || garbage === void 0 ? void 0 : garbage.orphanBytes) !== null && _d !== void 0 ? _d : 0), ")"] })] })] }), _jsxs("div", { className: "rdb-actions", children: [_jsxs("button", { className: `rdb-btn danger ${gcArmed ? 'armed' : ''}`, onClick: handleGc, disabled: busy || loading || !garbage || garbage.orphans === 0, children: [_jsx(FaBroom, {}), gcRunning ? 'Cleaning…'
                                                    : gcArmed ? `Really delete ${garbage === null || garbage === void 0 ? void 0 : garbage.orphans} files (${formatBytes((_e = garbage === null || garbage === void 0 ? void 0 : garbage.orphanBytes) !== null && _e !== void 0 ? _e : 0)})?`
                                                        : 'Clean unused files'] }), gcArmed && !gcRunning && (_jsx("button", { className: "rdb-btn", onClick: () => setGcArmed(false), children: "Cancel" }))] }), gcDone && _jsxs("p", { className: "rdb-done", children: ["Deleted ", gcDone.deleted, " files, freed ", formatBytes(gcDone.freedBytes), "."] })] }), _jsxs("div", { className: "rdb-section", children: [_jsx("h3", { children: "Data \u2014 levels a full reset would wipe" }), _jsxs("p", { className: "rdb-sub", children: ["Every non-demo level with all its content (", stats.disposableLevels.length, " level", stats.disposableLevels.length === 1 ? '' : 's', stats.disposableLevels.length > 0 && `: ${stats.disposableLevels.slice(0, 8).join(', ')}${stats.disposableLevels.length > 8 ? '…' : ''}`, "). The seeded demos survive."] }), stats.fullResetEnabled ? (_jsxs("div", { className: "rdb-actions", children: [_jsxs("button", { className: `rdb-btn danger ${resetArmed ? 'armed' : ''}`, onClick: handleFullReset, disabled: busy || loading || stats.disposableLevels.length === 0, children: [_jsx(FaTrash, {}), resetRunning ? 'Wiping…'
                                                    : resetArmed ? `Really wipe ${stats.disposableLevels.length} levels?`
                                                        : 'Full reset (dev)'] }), resetArmed && !resetRunning && (_jsx("button", { className: "rdb-btn", onClick: () => setResetArmed(false), children: "Cancel" }))] })) : (_jsx("p", { className: "rdb-locked", children: "Full reset is disabled on this installation (deploy-time code switch). Only garbage cleanup is available here." })), resetDone && _jsx("p", { className: "rdb-done", children: resetDone })] })] })), error && _jsx("p", { className: "rdb-error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsxs("button", { type: "button", onClick: refresh, disabled: busy || loading, children: [_jsx(FaSyncAlt, {}), " Refresh"] }), _jsx("button", { type: "button", onClick: onClose, disabled: busy, children: "Close" })] })] }) }));
}
export default ResetDbModal;
